import type { EndpointExpectedResponseObservationSchema } from '@terra/shared-contracts-endpoints';
import type { EndpointPreviewReturnDto } from '@terra/shared-contracts-endpoints';
import type { z } from 'zod';

export type ExpectedResponseObservation = z.infer<
  typeof EndpointExpectedResponseObservationSchema
>;

export type ExpectedResponseMatchResult =
  ExpectedResponseObservation['matchResult'];

const MATCH_RESULT_RANK: Record<ExpectedResponseMatchResult, number> = {
  full: 3,
  status_only: 2,
  unmatched: 1,
};

export type GroupedToolCallObservations = {
  toolCallId: string;
  primary: ExpectedResponseObservation;
  additionalAttempts: ExpectedResponseObservation[];
};

export type GroupedEndpointObservations = {
  endpointId: string;
  endpointLabel: string;
  toolCalls: GroupedToolCallObservations[];
};

export type ExpectedResponseObservationSummary = {
  full: number;
  statusOnly: number;
  unmatched: number;
  endpointCount: number;
};

export function summarizeExpectedResponseObservations(
  observations: ExpectedResponseObservation[],
): ExpectedResponseObservationSummary {
  const endpointIds = new Set<string>();
  let full = 0;
  let statusOnly = 0;
  let unmatched = 0;

  for (const observation of observations) {
    endpointIds.add(observation.endpointId);

    if (observation.matchResult === 'full') {
      full += 1;
      continue;
    }

    if (observation.matchResult === 'status_only') {
      statusOnly += 1;
      continue;
    }

    unmatched += 1;
  }

  return {
    full,
    statusOnly,
    unmatched,
    endpointCount: endpointIds.size,
  };
}

export function pickBestExpectedResponseObservation(
  observations: ExpectedResponseObservation[],
): ExpectedResponseObservation {
  return observations.reduce((best, current) => {
    const bestRank = MATCH_RESULT_RANK[best.matchResult];
    const currentRank = MATCH_RESULT_RANK[current.matchResult];

    if (currentRank > bestRank) {
      return current;
    }

    if (currentRank < bestRank) {
      return best;
    }

    return new Date(current.recordedAt).getTime() >
      new Date(best.recordedAt).getTime()
      ? current
      : best;
  });
}

export function groupExpectedResponseObservations({
  observations,
  endpointById,
}: {
  observations: ExpectedResponseObservation[];
  endpointById: Map<string, EndpointPreviewReturnDto>;
}): GroupedEndpointObservations[] {
  const byEndpoint = new Map<
    string,
    Map<string, ExpectedResponseObservation[]>
  >();

  for (const observation of observations) {
    const byToolCall =
      byEndpoint.get(observation.endpointId) ??
      new Map<string, ExpectedResponseObservation[]>();

    const toolCallObservations = byToolCall.get(observation.toolCallId) ?? [];
    toolCallObservations.push(observation);
    byToolCall.set(observation.toolCallId, toolCallObservations);
    byEndpoint.set(observation.endpointId, byToolCall);
  }

  return [...byEndpoint.entries()]
    .map(([endpointId, toolCallMap]) => {
      const endpoint = endpointById.get(endpointId);
      const endpointLabel = endpoint
        ? `${endpoint.request.method} ${endpoint.path}`
        : endpointId;

      const toolCalls = [...toolCallMap.entries()]
        .map(([toolCallId, toolCallObservations]) => {
          const sortedObservations = [...toolCallObservations].sort(
            (left, right) =>
              new Date(right.recordedAt).getTime() -
              new Date(left.recordedAt).getTime(),
          );
          const primary =
            pickBestExpectedResponseObservation(sortedObservations);
          const additionalAttempts = sortedObservations.filter(
            (observation) => observation.id !== primary.id,
          );

          return {
            toolCallId,
            primary,
            additionalAttempts,
          };
        })
        .sort((left, right) => left.toolCallId.localeCompare(right.toolCallId));

      return {
        endpointId,
        endpointLabel,
        toolCalls,
      };
    })
    .sort((left, right) =>
      left.endpointLabel.localeCompare(right.endpointLabel),
    );
}

export type ExpectedResponseAttempt = {
  id: string;
  toolCallId: string;
  toolExecutionHarId: string;
  observedStatus: number;
  matchResult: ExpectedResponseMatchResult;
  recordedAt: Date;
};

export type ExpectedResponseObservationTableRow = {
  id: string;
  endpointLabel: string;
  toolCallId: string;
  observedStatus: number;
  expectedAction: string | null;
  expectedEntity: string | null;
  isStateChanging: boolean | null;
  description: string | null;
  toolExecutionHarId: string;
  extraAttempts: number;
  // Every request attempt for this tool call (primary + retries), oldest
  // first. Each attempt is a distinct HTTP request/HAR under the same step,
  // so the UI can itemize retries and link each back to its producing step.
  attempts: ExpectedResponseAttempt[];
  matchResult: ExpectedResponseMatchResult;
  recordedAt: Date;
};

export function buildExpectedResponseObservationTableRows({
  observations,
  endpointById,
}: {
  observations: ExpectedResponseObservation[];
  endpointById: Map<string, EndpointPreviewReturnDto>;
}): ExpectedResponseObservationTableRow[] {
  return groupExpectedResponseObservations({
    observations,
    endpointById,
  }).flatMap((endpointGroup) =>
    endpointGroup.toolCalls.map((toolCallGroup) => {
      const matched = toolCallGroup.primary.matchedExpectedResponse;

      const attempts: ExpectedResponseAttempt[] = [
        toolCallGroup.primary,
        ...toolCallGroup.additionalAttempts,
      ]
        .map((observation) => ({
          id: observation.id,
          toolCallId: observation.toolCallId,
          toolExecutionHarId: observation.toolExecutionHarId,
          observedStatus: observation.observedStatus,
          matchResult: observation.matchResult,
          recordedAt: new Date(observation.recordedAt),
        }))
        .sort(
          (left, right) =>
            left.recordedAt.getTime() - right.recordedAt.getTime(),
        );

      return {
        id: toolCallGroup.primary.id,
        endpointLabel: endpointGroup.endpointLabel,
        toolCallId: toolCallGroup.toolCallId,
        observedStatus: toolCallGroup.primary.observedStatus,
        expectedAction: matched?.verb ?? null,
        expectedEntity: matched?.entity ?? null,
        isStateChanging: matched?.isStateChanging ?? null,
        description: matched?.description ?? null,
        toolExecutionHarId: toolCallGroup.primary.toolExecutionHarId,
        extraAttempts: toolCallGroup.additionalAttempts.length,
        attempts,
        matchResult: toolCallGroup.primary.matchResult,
        recordedAt: toolCallGroup.primary.recordedAt,
      };
    }),
  );
}

export function partitionExpectedResponseObservationRows(
  rows: ExpectedResponseObservationTableRow[],
): {
  stateChangingRows: ExpectedResponseObservationTableRow[];
  nonStateChangingRows: ExpectedResponseObservationTableRow[];
} {
  const stateChangingRows: ExpectedResponseObservationTableRow[] = [];
  const nonStateChangingRows: ExpectedResponseObservationTableRow[] = [];

  for (const row of rows) {
    if (row.isStateChanging === true) {
      stateChangingRows.push(row);
      continue;
    }

    nonStateChangingRows.push(row);
  }

  return { stateChangingRows, nonStateChangingRows };
}

export function sortExpectedResponseObservationRowsByRecordedAt(
  rows: ExpectedResponseObservationTableRow[],
): ExpectedResponseObservationTableRow[] {
  return [...rows].sort(
    (left, right) =>
      new Date(left.recordedAt).getTime() -
      new Date(right.recordedAt).getTime(),
  );
}

export type AuditLogEndpointFilterOption = {
  id: string;
  label: string;
};

export type AuditLogFlowFilterDimension =
  | 'endpoints'
  | 'verbs'
  | 'entities'
  | 'descriptions';

export type AuditLogFlowFilters = Record<AuditLogFlowFilterDimension, string[]>;

export type AuditLogFlowFilterOptions = {
  endpoints: AuditLogEndpointFilterOption[];
  verbs: string[];
  entities: string[];
  descriptions: string[];
};

export function hasActiveAuditLogFlowFilters(
  filters: AuditLogFlowFilters,
): boolean {
  return (
    filters.endpoints.length > 0 ||
    filters.verbs.length > 0 ||
    filters.entities.length > 0 ||
    filters.descriptions.length > 0
  );
}

export function filterAuditLogFlowRows({
  rows,
  filters,
}: {
  rows: ExpectedResponseObservationTableRow[];
  filters: AuditLogFlowFilters;
}): ExpectedResponseObservationTableRow[] {
  if (!hasActiveAuditLogFlowFilters(filters)) {
    return rows;
  }

  return rows.filter((row) => {
    if (
      filters.endpoints.length > 0 &&
      !filters.endpoints.includes(row.endpointLabel)
    ) {
      return false;
    }

    if (
      filters.verbs.length > 0 &&
      (!row.expectedAction || !filters.verbs.includes(row.expectedAction))
    ) {
      return false;
    }

    if (
      filters.entities.length > 0 &&
      (!row.expectedEntity || !filters.entities.includes(row.expectedEntity))
    ) {
      return false;
    }

    if (
      filters.descriptions.length > 0 &&
      (!row.description || !filters.descriptions.includes(row.description))
    ) {
      return false;
    }

    return true;
  });
}

export function formatExpectedResponseSnapshotOneLiner({
  observedStatus,
  matchResult,
  matchedExpectedResponse,
}: {
  observedStatus: number;
  matchResult: ExpectedResponseMatchResult;
  matchedExpectedResponse: ExpectedResponseObservation['matchedExpectedResponse'];
}): string {
  const matchLabel =
    matchResult === 'full'
      ? 'FULL'
      : matchResult === 'status_only'
        ? 'STATUS ONLY'
        : 'UNMATCHED';

  if (!matchedExpectedResponse) {
    return `${observedStatus} · ${matchLabel}`;
  }

  return `${observedStatus} · ${matchLabel} · ${matchedExpectedResponse.responseCode} ${matchedExpectedResponse.verb} ${matchedExpectedResponse.entity}`;
}
